import { h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { CHART_TYPE } from '@/_legacy/constants';
import { encodeInspectionQuery, InspectionQuery } from '@/services/api/resources/analytics/query';
import { col, cols } from '@/ui/components';
import NumberCard from '@/_legacy/views/Analytics/components/NumberCard.vue';
import { t, toUnixTimestamp } from '@/ui/i18n/i18n';
import { pushRoute } from '@/ui/helpers/uiHelper';
import GroupedChartCard from '@/_legacy/views/Analytics/components/GroupedChartCard.vue';
import { useTask } from '@/ui/concerns';
import { groupedInspectionList } from '@/services/api/resources/analytics';
import { InspectionGroupBy } from '@/services/api/resources/analytics/types';

interface OverdueInspectionsChartsProps {
  query: InspectionQuery;
  overdueInspectionCount: number;
  overdueInspectionPercentage: number;
}

export const OverdueInspectionsCharts = defineComponent<OverdueInspectionsChartsProps>({
  name: 'OverdueInspectionsCharts',
  props: ['query', 'overdueInspectionCount', 'overdueInspectionPercentage'],
  setup(props) {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const chartType = ref(CHART_TYPE.LINE);
    const chartGroupBy = ref(InspectionGroupBy.Week);
    const chartData = ref({});

    const unsetSort = (query: InspectionQuery) => {
      const queryNoSort = { ...query };
      queryNoSort.sort = '';
      return queryNoSort;
    };

    const getList = useTask(
      () => {
        initialLoading.value = true;
        return groupedInspectionList(chartGroupBy.value, unsetSort(props.query));
      },
      response => {
        const dateFormatted = (start: Date | null) => start ? toUnixTimestamp(start.getTime()) : null;

        chartData.value = {
          labels: response.data.map(result => dateFormatted(result.group.start)),
          series: [response.data.map((result, index) => ({
            x: index,
            y: result.overdueInspectionCount,
            meta: {
              start: dateFormatted(result.group.start),
              end: dateFormatted(result.group.end),
            },
          }))],
          format: 'analyticsInteger',
        };

        initialLoading.value = false;
      },
    );

    watch([
      () => props.query,
      chartGroupBy,
    ], getList.run, { deep: true });

    const redirectToExplain = (query: InspectionQuery) => pushRoute({
      name: 'analyticsOverdueInspectionsExplain',
      query: encodeInspectionQuery(query),
    });

    return function render() {
      return h('div', [
        cols([
          col([
            h(NumberCard, {
              props: {
                loading: initialLoading.value,
                color: 'var(--analytics-blue)',
                label: t('analytics.overdue-count'),
                number: props.overdueInspectionCount,
                percentage: props.overdueInspectionPercentage,
              },
              class: ['h-full'],
              on: {
                numberClick: () => redirectToExplain(unsetSort(props.query)),
              },
            }),
          ], true, { 'number-column': true }),
          col([
            //legacy, not compatible with Vue typings
            //@ts-ignore
            h(GroupedChartCard, {
              class: ['w-full'],
              props: {
                color: 'blue',
                loading: initialLoading.value,
                data: chartData.value,
                startsAt: toUnixTimestamp(props.query.start.getTime()),
                endsAt: toUnixTimestamp(props.query.end.getTime()),
                type: chartType.value,
                groupBy: chartGroupBy.value,
                tooltipText: t('analytics.overdue-inspections'),
              },
              on: {
                typeChanged: (newType: CHART_TYPE) => {
                  chartType.value = newType;
                },
                groupByChanged: (newGroup: InspectionGroupBy) => {
                  chartGroupBy.value = newGroup;
                },
              },
            }),
          ]),
        ]),
      ]);
    };
  },
});
